import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../redux/store";
import { listenToShortstayData, upsertShortstay } from "../../../../redux/shortstaysSlice";
import { CircularProgress } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";

import HomeWorkIcon from "@mui/icons-material/HomeWork";
import GroupIcon from "@mui/icons-material/Group";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import BedIcon from "@mui/icons-material/Bed";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import DateRangeIcon from "@mui/icons-material/DateRange";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";

type ExtraItem = { name: string; price: number };

type RateType =
  | "perNight"
  | "perNightPerPerson"
  | "perHour"
  | "perHourPerPerson"
  | "perWeek"
  | "perMonth"
  | "perYear";

const ShortForm7: React.FC = () => {
  const { userId, listingId } = useParams<{ userId: string; listingId: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const currentListing = useSelector((s: RootState) => s.shortstays.currentListing);

  const [rateType, setRateType] = useState<RateType>("perNight");
  const [rate, setRate] = useState(5);
  const [extras, setExtras] = useState<ExtraItem[]>([]);
  const [newExtraName, setNewExtraName] = useState("");
  const [newExtraPrice, setNewExtraPrice] = useState(0);
  const [isUpdating, setIsUpdating] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (listingId) dispatch(listenToShortstayData(listingId));
  }, [dispatch, listingId]);

  useEffect(() => {
    if (!currentListing) return;
    if (currentListing.rate) setRate(currentListing.rate);
    if (currentListing.rateType) setRateType(currentListing.rateType);
    if (currentListing.extras) {
      setExtras(
        Object.entries(currentListing.extras).map(([name, price]) => ({
          name,
          price: Number(price),
        }))
      );
    }
  }, [currentListing]);

  const extraCharges = extras.reduce((s, e) => s + e.price, 0);

  const addExtra = () => {
    if (!newExtraName.trim() || newExtraPrice <= 0) return;
    if (extras.some(e => e.name.toLowerCase() === newExtraName.toLowerCase())) return;
    setExtras(prev => [...prev, { name: newExtraName.trim(), price: newExtraPrice }]);
    setNewExtraName("");
    setNewExtraPrice(0);
  };

  const handleSave = async () => {
    if (!listingId) return;
    setIsUpdating(true);

    const extrasObj: Record<string, number> = {};
    extras.forEach((e) => (extrasObj[e.name] = e.price));

    await dispatch(
      upsertShortstay({
        listingId,
        data: { rate, rateType, extras: extrasObj, extraCharges },
      })
    );

    setSuccess(true);
    setTimeout(() => navigate(`/shortform8/${userId}/${listingId}`), 1000);
  };

  // label shown next to the big rate
  const rateLabel: Record<RateType, string> = {
    perNight:          "/ night",
    perNightPerPerson: "/ night · person",
    perHour:           "/ hour",
    perHourPerPerson:  "/ hour · person",
    perWeek:           "/ week",
    perMonth:          "/ month",
    perYear:           "/ year",
  };

  return (
    <div style={styles.pageWrapper}>
      {(isUpdating || success) && (
        <div style={styles.overlay}>
          {isUpdating && <CircularProgress sx={{ color: "#fff" }} />}
          {success && <CheckCircleOutlineIcon sx={{ color: "limegreen", fontSize: 80 }} />}
        </div>
      )}

      <div style={styles.container}>
        <h2 style={styles.title}>Pricing</h2>
        <p style={styles.subtitle}>How guests are charged</p>

        {/* RESPONSIVE GRID */}
        <div style={styles.responsiveGrid}>

          {/* HOW YOU CHARGE */}
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>How You Charge</h3>

            <div style={styles.rateTypeGrid}>
              <RateTypeCard
                icon={<HomeWorkIcon />}
                title="Per Night"
                active={rateType === "perNight"}
                onClick={() => setRateType("perNight")}
              />
              <RateTypeCard
                icon={<GroupIcon />}
                title="Per Night / Person"
                active={rateType === "perNightPerPerson"}
                onClick={() => setRateType("perNightPerPerson")}
              />
              <RateTypeCard
                icon={<AccessTimeIcon />}
                title="Per Hour"
                active={rateType === "perHour"}
                onClick={() => setRateType("perHour")}
              />
              <RateTypeCard
                icon={<BedIcon />}
                title="Per Hour / Person"
                active={rateType === "perHourPerPerson"}
                onClick={() => setRateType("perHourPerPerson")}
              />
              <RateTypeCard
                icon={<DateRangeIcon />}
                title="Per Week"
                active={rateType === "perWeek"}
                onClick={() => setRateType("perWeek")}
              />
              <RateTypeCard
                icon={<CalendarMonthIcon />}
                title="Per Month"
                active={rateType === "perMonth"}
                onClick={() => setRateType("perMonth")}
              />
              <RateTypeCard
                icon={<EventRepeatIcon />}
                title="Per Year"
                active={rateType === "perYear"}
                onClick={() => setRateType("perYear")}
              />
            </div>
          </div>

          {/* BASE RATE */}
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>Base Rate</h3>

            <div style={styles.bigRateWrapper}>
              <span style={styles.bigRate}>€{rate}</span>
              <span style={styles.rateUnitLabel}>{rateLabel[rateType]}</span>
            </div>

            <div style={styles.priceRow}>
              <button style={styles.roundBtn} onClick={() => setRate((r) => Math.max(5, r - 10))}>
                <RemoveIcon />
              </button>
              <input
                type="number"
                value={rate}
                onChange={(e) => setRate(Math.max(5, Number(e.target.value)))}
                style={styles.priceInput}
              />
              <button style={styles.roundBtn} onClick={() => setRate((r) => r + 10)}>
                <AddIcon />
              </button>
            </div>
          </div>

          {/* EXTRAS */}
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>Extra Charges (€{extraCharges})</h3>

            <div style={styles.extraRow}>
              <input
                style={styles.extraNameInput}
                placeholder="Cleaning Fee then Press Add"
                value={newExtraName}
                onChange={(e) => setNewExtraName(e.target.value)}
              />
              <input
                style={styles.extraPriceInput}
                type="number"
                placeholder="€"
                value={newExtraPrice}
                onChange={(e) => setNewExtraPrice(Number(e.target.value))}
              />
              <button style={styles.addBtn} onClick={addExtra}>+</button>
            </div>

            {extras.map((item, index) => (
              <div key={index} style={styles.extraRow}>
                <input value={item.name} style={styles.extraNameInput} readOnly />
                <input value={item.price} style={styles.extraPriceInput} readOnly />
                <button
                  style={{ ...styles.addBtn, background: "#444" }}
                  onClick={() => setExtras(prev => prev.filter((_, i) => i !== index))}
                >
                  ×
                </button>
              </div>
            ))}
          </div>

        </div>
      </div>

      <footer style={styles.footer}>
        <button onClick={() => navigate(-1)} style={styles.backBtn}>Back</button>
        <button onClick={handleSave} style={styles.nextBtn}>Next</button>
      </footer>
    </div>
  );
};

export default ShortForm7;

const RateTypeCard = ({ icon, title, active, onClick }: any) => (
  <div
    onClick={onClick}
    style={{
      ...styles.rateTypeCard,
      borderColor: active ? "#bf9b30" : "#333",
      boxShadow: active ? "0 0 18px rgba(191,155,48,.4)" : "none",
      background: active ? "rgba(191,155,48,0.08)" : "transparent",
    }}
  >
    <div style={styles.rateIcon}>{icon}</div>
    <div>{title}</div>
  </div>
);

const styles: any = {
  pageWrapper: { background: "#000", color: "#fff", minHeight: "100vh", paddingBottom: 120 },
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 },

  container: { padding: 20, maxWidth: 1200, margin: "0 auto" },
  title: { fontSize: 26, fontWeight: 700 },
  subtitle: { color: "#bbb", marginBottom: 20 },

  responsiveGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
    gap: 20,
  },

  card: {
    background: "#111",
    borderRadius: 16,
    padding: 24,
    boxShadow: "0 0 10px rgba(255,255,255,.1)",
  },

  cardTitle: { fontSize: 18, fontWeight: 700, marginBottom: 16 },

  rateTypeGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
    gap: 12,
  },

  rateTypeCard: {
    border: "2px solid",
    borderRadius: 14,
    padding: 14,
    display: "flex",
    gap: 10,
    alignItems: "center",
    cursor: "pointer",
    transition: "all .2s",
  },

  rateIcon: { color: "#bf9b30", fontSize: 26 },

  bigRateWrapper: { textAlign: "center", marginBottom: 16 },
  bigRate: { fontSize: 56, fontWeight: 900, textShadow: "3px 3px 0 #bf9b30" },
  rateUnitLabel: { display: "block", color: "#bf9b30", fontWeight: 700, fontSize: 16, marginTop: 4 },

  priceRow: { display: "flex", justifyContent: "center", gap: 8 },
  roundBtn: { background: "#fff", borderRadius: "50%", width: 40, height: 40, border: "none", cursor: "pointer" },
  priceInput: { width: 120, fontSize: 26, textAlign: "center", borderRadius: 12, border: "2px solid #bf9b30", background: "#000", color: "#fff" },

  extraRow: {
    display: "grid",
    gridTemplateColumns: "1fr 80px auto",
    gap: 10,
    marginBottom: 10,
  },

  extraNameInput: {
    padding: 12,
    borderRadius: 10,
    border: "1px solid #ccc",
    fontSize: 14,
    background: "#1a1a1a",
    color: "#fff",
  },

  extraPriceInput: {
    padding: 12,
    borderRadius: 10,
    border: "1px solid #ccc",
    textAlign: "center",
    background: "#1a1a1a",
    color: "#fff",
  },

  addBtn: {
    background: "#bf9b30",
    color: "#fff",
    borderRadius: 10,
    padding: "0 16px",
    border: "none",
    fontSize: 20,
    cursor: "pointer",
  },

  footer: {
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    background: "#000",
    borderTop: "1px solid #333",
    padding: 20,
    display: "flex",
    justifyContent: "space-between",
  },

  backBtn: { background: "#222", color: "#fff", borderRadius: 30, padding: "10px 22px", border: "none", cursor: "pointer" },
  nextBtn: { background: "linear-gradient(to right,#fff,#bf9b30)", borderRadius: 30, padding: "10px 22px", fontWeight: 700, border: "none", cursor: "pointer" },
};